import { z } from 'zod';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { VerdocsSpinner } from '@verdocs/web-sdk-react';
import { FormProvider, useForm } from 'react-hook-form';
import { IOrganizationInvitation, updateOrganizationInvitation, VerdocsEndpoint } from '@verdocs/js-sdk';
import { getCallerAccess } from '../Views/Settings/Components/memberAccess';
import { Button, SelectInput } from '../Components';
import { Dialog } from './Dialog';

const schema = z.object({
  role: z.enum([ 'owner', 'admin', 'member', 'basic_user', 'contact' ], { error: 'Please select a role' }),
});

type FormValues = z.infer<typeof schema>;

interface DialogProps {
  invite: IOrganizationInvitation;
  onClose?: () => void;
}

export function EditInviteDialog({ invite, onClose = () => {} }: DialogProps) {
  const [ submitting, setSubmitting ] = useState(false);
  const { assignableRoleOptions } = getCallerAccess();

  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: { role: invite.role === 'basic_user' ? 'member' : invite.role },
  });

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true);
    await updateOrganizationInvitation(VerdocsEndpoint.getDefault(), invite.email, { role: data.role })
      .then(() => {
        toast.success('Invitation updated.');
        setSubmitting(false);
        onClose();
      })
      .catch((e: any) => {
        console.log('Error updating invitation', e);
        toast.error(e.response?.data?.error || 'Error updating the invitation, please try again later.');
        setSubmitting(false);
      });
  };

  return (
    <Dialog onCancel={onClose}>
      <div className="mb-1 text-lg font-medium text-[#33364b]">Edit invitation</div>
      <div className="mb-4 text-[13px] leading-normal text-gray-500">
        {[ invite.first_name, invite.last_name ].filter(Boolean).join(' ') || invite.email}
        {(invite.first_name || invite.last_name) && ` (${invite.email})`}
      </div>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <SelectInput
            options={assignableRoleOptions}
            name="role"
            label="Role"
          />
          <div className="mt-2 text-[12px] leading-normal text-gray-500">The role that will apply when they accept.</div>

          <div className="mt-5 flex flex-row items-center justify-end gap-2">
            {submitting && <VerdocsSpinner mode="dark" />}
            <Button
              type="button"
              size="small"
              variant="secondary"
              label="Cancel"
              onClick={onClose}
              disabled={submitting}
            />
            <Button
              size="small"
              label="Save"
              type="submit"
              disabled={submitting || !form.formState.isDirty}
            />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
}
